import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Check } from 'lucide-react'
import { Panel, Button, Field, FormRow, Switch, Banner, useToast } from '../../ds/index'
import { Icon } from '../../components/Icon'
import { useApi } from '../../lib/useApi'
import { patch } from '../../lib/api'
import type { Org } from '../../lib/types'
import { LoadingBlock, ErrorBlock } from '../../components/Loading'

export default function EmailSettings() {
  const org = useApi<{ org: Org }>('/api/settings')
  const conn = useApi<{ email: { ready: boolean; from: string | null; sent_30d: number; failed_30d: number } }>('/api/connections')
  const toast = useToast()
  const [f, setF] = useState({ email_from_name: '', email_reply_to: '', support_phone: '', email_show_post_text: false, monthly_summary: true })
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (org.data) {
      const o = org.data.org
      setF({ email_from_name: o.email_from_name || '', email_reply_to: o.email_reply_to || '', support_phone: (o as Org & { support_phone?: string | null }).support_phone || '', email_show_post_text: o.email_show_post_text, monthly_summary: o.monthly_summary })
    }
  }, [org.data])
  if (org.error) return <ErrorBlock error={org.error} />
  if (!org.data) return <LoadingBlock />
  const name = org.data.org.name
  const e = conn.data?.email

  const save = async () => {
    setBusy(true)
    try { await patch('/api/settings', f); toast('Saved', 'pos'); org.reload() } catch (err) { toast((err as Error).message, 'neg') } finally { setBusy(false) }
  }

  return (
    <>
      {e && (
        <div style={{ marginBottom: 'var(--s-6)' }}>
          {e.ready
            ? <Banner tone="pos" title="Sending is on">From {e.from}. {e.sent_30d} sent in the last 30 days{e.failed_30d ? `, ${e.failed_30d} failed` : ''}.</Banner>
            : <Banner tone="warn" title="Sending is not set up yet">Every email is still written and recorded as not sent, so nothing is lost and nothing reaches a subscriber. SPEC turns sending on once {name}'s email domain is verified.</Banner>}
        </div>
      )}
      <div className="two-col">
        <Panel>
          <div className="panel-header"><h2 className="panel-title">What clients get</h2></div>
          <ul className="mailbox-points">
            <li><Icon of={Check} /><span><strong>A portal login.</strong> Their recommendations, the "did they reach out?" answer, balance and payments, disputes, documents and their business profile, all in one place. They choose a password from the welcome link sent when they are added.</span></li>
            <li><Icon of={Check} /><span><strong>An email each time a rep recommends them.</strong> What the person needed, the area and timing, a screenshot of the reply, and a button that opens that lead in their portal.</span></li>
            <li><Icon of={Check} /><span><strong>A monthly summary</strong> on the 1st: how many times they were recommended, in how many groups, and how many people reached out, with a link to their portal.</span></li>
          </ul>
          <p className="mailbox-note" style={{ margin: 'var(--s-5) 0 0' }}>Each client can get every email, the monthly summary only, or none. Set that, choose which contacts get lead emails, and preview their emails on <Link to="/clients">Clients</Link>.</p>
        </Panel>

        <Panel>
          <div className="panel-header"><h2 className="panel-title">How they are sent</h2></div>
          <div className="stack-tight">
            <Field label="From name" placeholder={name} value={f.email_from_name} onChange={(ev) => setF({ ...f, email_from_name: ev.target.value })} />
            <Field label="Replies go to" type="email" placeholder="dylan@yourcompany.com" value={f.email_reply_to} onChange={(ev) => setF({ ...f, email_reply_to: ev.target.value })} />
            <Field label="Phone for clients (optional)" type="tel" placeholder="(480) 555-0100" value={f.support_phone} onChange={(ev) => setF({ ...f, support_phone: ev.target.value })} />
            <p className="form-help" style={{ margin: 0 }}>Clients see this email and phone under "Questions?" in their portal.</p>
            <FormRow label="The poster's own words" help="Off by default. Some posts are about moving a parent into care. The poster's name is never shown either way.">
              <Switch label={f.email_show_post_text ? 'Shown in emails and on the page' : 'Hidden: only the need, area and timing'} checked={f.email_show_post_text} onChange={(ev) => setF({ ...f, email_show_post_text: ev.target.checked })} />
            </FormRow>
            <FormRow label="Monthly summary">
              <Switch label={f.monthly_summary ? 'Sent on the 1st of each month' : 'Not sent'} checked={f.monthly_summary} onChange={(ev) => setF({ ...f, monthly_summary: ev.target.checked })} />
            </FormRow>
            <div><Button onClick={save} disabled={busy}>{busy ? 'Saving' : 'Save email settings'}</Button></div>
          </div>
        </Panel>
      </div>
    </>
  )
}
